import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarIcon, ArrowRightIcon, TagIcon } from 'lucide-react';
import { Button } from '../components/Button';
interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  category: string;
  image: string;
  featured?: boolean;
}
const posts: BlogPost[] = [{
  slug: 'how-to-access-home-equity-in-canada',
  title: 'How to Access Your Home Equity in Canada: A Complete Guide',
  excerpt: "Millions of Canadian homeowners are sitting on significant equity but don't know how to access it. This guide walks you through every option available, from HELOCs to second mortgages, and helps you choose the right path.",
  date: 'February 18, 2026',
  category: 'Home Equity',
  image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1400&q=85'
}, {
  slug: 'second-mortgage-vs-heloc',
  title: 'Second Mortgage vs. HELOC: Which Is Right for You?',
  excerpt: "Both products let you tap into your home's equity, but they work very differently. We break down the key differences in rates, flexibility, and repayment, so you can make an informed decision.",
  date: 'February 10, 2026',
  category: 'Mortgages',
  image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1400&q=85'
}, {
  slug: 'debt-consolidation-with-home-equity',
  title: 'Using Home Equity to Consolidate Debt: Is It Worth It?',
  excerpt: "High-interest credit card debt can be crushing. Many homeowners use their equity to pay it all off at a much lower rate. Here's what you need to know before you do.",
  date: 'January 28, 2026',
  category: 'Debt Consolidation',
  image: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=1400&q=85'
}, {
  slug: 'reverse-mortgage-guide-canada',
  title: 'Reverse Mortgages in Canada: Everything Homeowners 55+ Need to Know',
  excerpt: "A reverse mortgage can provide tax-free income without requiring you to sell your home. But it's not for everyone. We cover eligibility, costs, and the key questions to ask.",
  date: 'January 15, 2026',
  category: 'Reverse Mortgages',
  image: 'https://images.unsplash.com/photo-1516733968668-dbdce39c4651?w=1400&q=85'
}, {
  slug: 'home-renovation-financing-options',
  title: '5 Ways to Finance Your Home Renovation in 2026',
  excerpt: "Planning a kitchen remodel or basement suite? Your home's equity could be the most affordable financing tool available. We compare all your options side by side.",
  date: 'January 5, 2026',
  category: 'Home Renovation',
  image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1400&q=85'
}, {
  slug: 'gta-real-estate-equity-trends-2026',
  title: 'GTA Real Estate Equity Trends: What Homeowners Should Know in 2026',
  excerpt: "Toronto-area property values have created substantial equity for many homeowners. Here's a look at current market conditions and what they mean for your borrowing power.",
  date: 'December 20, 2025',
  category: 'Market Insights',
  image: 'https://images.unsplash.com/photo-1486325212027-8081e485255e?w=1400&q=85'
}];
const categoryColors: Record<string, string> = {
  'Home Equity': 'bg-emerald-100 text-emerald-800',
  Mortgages: 'bg-blue-100 text-blue-800',
  'Debt Consolidation': 'bg-orange-100 text-orange-800',
  'Reverse Mortgages': 'bg-purple-100 text-purple-800',
  'Home Renovation': 'bg-yellow-100 text-yellow-800',
  'Market Insights': 'bg-gray-100 text-gray-800'
};
export function BlogPage() {
  const featured = posts[0];
  const rest = posts.slice(1);
  const POSTS_PER_PAGE = 3;
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(rest.length / POSTS_PER_PAGE);
  const paginatedPosts = rest.slice((currentPage - 1) * POSTS_PER_PAGE, currentPage * POSTS_PER_PAGE);
  return <main className="w-full" data-id="element-304">
      {/* Hero */}
      <section className="w-full bg-gradient-to-br from-[#07493a] to-[#1a6b55] py-20 px-4" data-id="element-305">
        <div className="max-w-7xl mx-auto text-center" data-id="element-306">
          <span className="inline-block px-4 py-2 text-[12px] font-[Poppins,_sans-serif] text-[rgb(212,168,83)] bg-[hsla(0,0%,100%,.1)] font-medium rounded-full mb-6 border border-white/20" data-id="element-307">
            INSIGHTS & RESOURCES
          </span>
          <h1 className="text-4xl md:text-6xl font-serif text-white mb-6 font-[500]" data-id="element-308">
            The EquityRich Blog
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto" data-id="element-309">
            Expert guidance on home equity, mortgages, and smart financial
            decisions for Canadian homeowners.
          </p>
        </div>
      </section>

      {/* Featured Post */}
      <section className="w-full bg-cream py-16 px-4" data-id="element-310">
        <div className="max-w-7xl mx-auto" data-id="element-311">
          <div className="mb-10" data-id="element-312"></div>

          <Link to={`/blog/${featured.slug}`} className="group grid grid-cols-1 lg:grid-cols-2 gap-0 bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300" data-id="element-313">
            <div className="relative overflow-hidden h-72 lg:h-auto" data-id="element-314">
              <img src={featured.image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-315" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" data-id="element-316" />
            </div>
            <div className="p-8 md:p-12 flex flex-col justify-center" data-id="element-317">
              <span className={`inline-block self-start px-3 py-1 text-xs font-semibold rounded-full mb-4 ${categoryColors[featured.category]}`} data-id="element-318">
                {featured.category}
              </span>
              <h2 className="text-2xl md:text-3xl font-serif text-gray-900 font-[500] mb-4 group-hover:text-primary transition-colors leading-snug" data-id="element-319">
                {featured.title}
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6" data-id="element-320">
                {featured.excerpt}
              </p>
              <div className="flex items-center text-sm text-gray-500 mb-6 space-x-4" data-id="element-321">
                <span className="flex items-center" data-id="element-322">
                  <CalendarIcon className="h-4 w-4 mr-1.5" aria-hidden="true" data-id="element-323" />
                  {featured.date}
                </span>
              </div>
              <span className="inline-flex items-center font-medium text-primary group-hover:text-accent transition-colors" data-id="element-324">
                Read Article
                <ArrowRightIcon className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" data-id="element-325" />
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* Post Grid */}
      <section className="w-full bg-cream pb-20 px-4" data-id="element-326">
        <div className="max-w-7xl mx-auto" data-id="element-327">
          <h2 className="text-2xl font-serif text-gray-900 font-[500] mb-8" data-id="element-328">
            Latest Articles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-329">
            {paginatedPosts.map(post => <Link key={post.slug} to={`/blog/${post.slug}`} className="group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col" data-id="element-330">
                <div className="relative overflow-hidden h-52" data-id="element-331">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-332" />
                  <div className="absolute top-4 left-4" data-id="element-333">
                    <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${categoryColors[post.category] || 'bg-gray-100 text-gray-800'}`} data-id="element-334">
                      {post.category}
                    </span>
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1" data-id="element-335">
                  <h3 className="text-lg font-serif text-gray-900 font-[500] mb-3 group-hover:text-primary transition-colors leading-snug" data-id="element-336">
                    {post.title}
                  </h3>
                  <div className="flex items-center text-xs text-gray-500 pt-4 border-t border-gray-100 space-x-3 mt-auto" data-id="element-337">
                    <span className="flex items-center" data-id="element-338">
                      <CalendarIcon className="h-3.5 w-3.5 mr-1" aria-hidden="true" data-id="element-339" />
                      {post.date}
                    </span>
                  </div>
                </div>
              </Link>)}
          </div>

          {/* Pagination */}
          {totalPages > 1 && <div className="flex items-center justify-center gap-2 mt-14" data-id="element-340">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 text-gray-600 hover:border-primary hover:text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-341">
                ← Prev
              </button>
              {Array.from({
            length: totalPages
          }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${page === currentPage ? 'bg-primary text-white shadow-sm' : 'border border-gray-200 text-gray-600 hover:border-primary hover:text-primary'}`} data-id="element-342">
                  {page}
                </button>)}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 text-gray-600 hover:border-primary hover:text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-343">
                Next →
              </button>
            </div>}
        </div>
      </section>

      {/* CTA */}
      <section className="w-full relative overflow-hidden bg-gradient-to-br from-[#07493a] to-[#1a6b55] py-16 px-4" data-id="element-344">
        <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse 60% 80% at 80% 20%, rgba(201,169,97,0.15) 0%, rgba(201,169,97,0.06) 40%, transparent 70%)'
      }} aria-hidden="true" data-id="element-345" />
        <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-346">
          <h2 className="text-3xl md:text-4xl font-serif text-white font-[500] mb-4" data-id="element-347">
            Ready to Unlock Your Home's Equity?
          </h2>
          <p className="text-gray-300 text-lg mb-8" data-id="element-348">
            Get a free assessment in minutes — no credit check required.
          </p>
          <Button variant="primary" size="lg" icon href="/#form" data-id="element-349">
            Get My Free Assessment
          </Button>
        </div>
      </section>
    </main>;
}